// Pipeline test - auto build and deploy
import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { OrgProvider, useOrg } from '@/contexts/OrgContext'
import { usePermissions } from '@/hooks/usePermissions'
import { CadenceProvider } from '@/contexts/CadenceContext'
import { WorkflowProvider } from '@/contexts/WorkflowContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { MainLayout } from '@/components/layout/MainLayout'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  Agents,
  Auth,
  Dashboard,
  Cadences,
  CadenceBuilder,
  Leads,
  LinkedInInbox,
  Settings,
  Templates,
  Admin,
  AdminLogs,
  AdminMetrics,
} from '@/pages'
import { AuthCallback } from '@/pages/AuthCallback'
import { LeadStepExecution } from '@/pages/LeadStepExecution'
import { CadenceFlow } from '@/pages/CadenceFlow'
import { AIPrompts } from '@/pages/AIPrompts'
import { Onboarding } from '@/pages/Onboarding'
import { Workflows } from '@/pages/Workflows'
import { WorkflowBuilder } from '@/pages/WorkflowBuilder'
import { WorkflowRuns } from '@/pages/WorkflowRuns'
import { Notifications } from '@/pages/Notifications'
import { AccountMapping } from '@/pages/AccountMapping'
import { AccountMapDetail } from '@/pages/AccountMapDetail'
import { ICPProfileDetail } from '@/pages/ICPProfileDetail'
import { CompanyRegistry } from '@/pages/CompanyRegistry'
import { BuyerPersonas } from '@/pages/BuyerPersonas'
import { AccountMappingProvider } from '@/contexts/AccountMappingContext'
import { CompanyResearchProvider } from '@/contexts/CompanyResearchContext'
import { BusinessCasesProvider } from '@/contexts/BusinessCasesContext'
import { PageErrorBoundary } from '@/components/PageErrorBoundary'
import { OrgSelect } from '@/pages/OrgSelect'
import { AcceptInvite } from '@/pages/AcceptInvite'
import { OrgSettings } from '@/pages/OrgSettings'
import { OrgMembers } from '@/pages/OrgMembers'
import { SuperAdminOrgs } from '@/pages/SuperAdminOrgs'
import { FeatureRoute } from '@/components/FeatureRoute'
import { SalesforceCallback } from '@/pages/SalesforceCallback'
import { OutreachActivity } from '@/pages/OutreachActivity'
import { OutreachControl } from '@/pages/OutreachControl'
import { AutomCadence } from '@/pages/AutomCadence'
import { NewAutomCadence } from '@/pages/NewAutomCadence'
import { CompanyResearch } from '@/pages/CompanyResearch'
import { ResearchProjectDetail } from '@/pages/ResearchProjectDetail'
import { BusinessCases } from '@/pages/BusinessCases'
import { BusinessCaseNew } from '@/pages/BusinessCaseNew'
import { Presentaciones } from '@/pages/Presentaciones'
import { BusinessCaseTemplateEditor } from '@/pages/BusinessCaseTemplateEditor'
import { BusinessCaseGenerate } from '@/pages/BusinessCaseGenerate'
import { AccountExecutive } from '@/pages/AccountExecutive'
import { AccountExecutiveCalendar } from '@/pages/AccountExecutiveCalendar'
import { AccountExecutiveDetail } from '@/pages/AccountExecutiveDetail'
import { CRMPipeline } from '@/pages/CRMPipeline'
import { AccountExecutiveProvider } from '@/contexts/AccountExecutiveContext'
import { AgentProvider } from '@/contexts/AgentContext'
import { ModeProvider } from '@/contexts/ModeContext'
import { LeadSearch } from '@/pages/LeadSearch'
import { AgentDetail } from '@/pages/AgentDetail'
import { AppLauncher } from '@/pages/AppLauncher'
import { MissionControl } from '@/pages/MissionControl'
import { TarjetaPresente } from '@/pages/TarjetaPresente'
import Chat from '@/pages/Chat'

// SS deck (Stripe Sessions style decks at /m/:slug). Lazy-loaded so the
// 21 slide components + 40MB of assets don't inflate the main bundle.
const SSDeckRoute = lazy(() => import('@/ss-deck/SSDeckRoute'))
const SSDeckPrintRoute = lazy(() => import('@/ss-deck/SSDeckPrintRoute'))

// Workshop BC deck (/workshop/:slug). Lazy-loaded — reuses ss-deck tokens
// and primitives but has its own 17-slide deck for workshop-grade BCs.
const WorkshopRoute = lazy(() => import('@/workshops-bc/WorkshopRoute'))
const WorkshopPrintRoute = lazy(() => import('@/workshops-bc/WorkshopPrintRoute'))

// Pricing-only mini-deck (/pricing/:slug). Reads the same workshops_bc
// row but renders just the 7 commercial-proposal slides (cover · CNP ·
// CP/POS · included features · add-ons · NOVA+concierge · closing).
const PricingRoute = lazy(() => import('@/workshops-bc/PricingRoute'))
const PricingPrintRoute = lazy(() => import('@/workshops-bc/PricingPrintRoute'))
// Propuesta única (/propuesta/:slug) — 3-slide consolidated proposal, same
// workshops_bc row as the pricing/workshop deck.
const ProposalRoute = lazy(() => import('@/workshops-bc/ProposalRoute'))
const ProposalPrintRoute = lazy(() => import('@/workshops-bc/ProposalPrintRoute'))
// Propuesta simple (/precios/:slug) — 3 slides: portada + 3 líneas de costo
// (tx flat + SaaS + conciliación) + equipo. Mismo row de workshops_bc.
const PreciosRoute = lazy(() => import('@/workshops-bc/PreciosRoute'))
const PreciosPrintRoute = lazy(() => import('@/workshops-bc/PreciosPrintRoute'))
// Business case BanCoppel (/bc-bancoppel) — 6 slides: MDR 1.70% → 1.50% sobre
// 6M tx/mes vs. el pricing escalonado de Yuno. Sin slug: los inputs son fijos
// (workshops-bc/lib/bancoppel-math), no lee workshops_bc.
const BancoppelRoute = lazy(() => import('@/workshops-bc/BancoppelRoute'))
const BancoppelPrintRoute = lazy(() => import('@/workshops-bc/BancoppelPrintRoute'))
// Propuesta comercial BanCoppel 2027 (/propuesta-bancoppel) — 10 slides:
// rate único $0.18 MXN/tx (piso = 1 centavo USD) + licencia SaaS $20K USD/mes
// + fee de implementación ($50K USD si Yuno embebe su SDK en la terminal, $0
// back-to-back) + mínimo transaccional (1M en mar-2027, +500K/mes, techo 4M) y
// el caso de negocio que lo justifica (MDR 1.60% → 1.45% + 2 pp de aprobación
// sobre 7M tx/mes). OJO: el pricing escalonado de bancoppel-math NO se toca —
// ese sigue siendo el de /bc-bancoppel. Sin slug: los inputs son fijos
// (workshops-bc/lib/bancoppel-2027-math), no lee workshops_bc.
const PropuestaBancoppelRoute = lazy(() => import('@/workshops-bc/PropuestaBancoppelRoute'))
const PropuestaBancoppelPrintRoute = lazy(() => import('@/workshops-bc/PropuestaBancoppelPrintRoute'))
// Propuesta comercial Grupo Quálitas (/qualitas) — 3 slides: portada + pricing
// (fee fijo + tx exitosa con mínimo 25K) + cierre. Sin slug: los precios son
// fijos y viven en SlideQualitasPricing, no lee workshops_bc.
const QualitasRoute = lazy(() => import('@/workshops-bc/QualitasRoute'))
const QualitasPrintRoute = lazy(() => import('@/workshops-bc/QualitasPrintRoute'))
// Deck interno C-Level (/in-person-payments) — 11 slides: apertura de la
// vertical de orquestación tarjeta presente a partir del deal de BanCoppel.
// Sin slug: los inputs son fijos (workshops-bc/lib/inperson-math).
const InPersonRoute = lazy(() => import('@/workshops-bc/InPersonRoute'))
const InPersonPrintRoute = lazy(() => import('@/workshops-bc/InPersonPrintRoute'))
// Deck interno C-Level (/in-person-payments/next-steps) — 10 slides: next
// steps de la vertical para Q4 2026. Demanda viva (Hertz, HRS, BanCoppel,
// Quálitas + discovery McDonald's/Redeban), riesgo de vender sin producto, y
// las cuatro decisiones que se piden al comité (sponsorship de Producto, KPI
// $2M ARR 2027, enablement regional, rol de VP). Sin slug: datos fijos en
// workshops-bc/lib/ippnext-math.
const InPersonNextRoute = lazy(() => import('@/workshops-bc/InPersonNextRoute'))
const InPersonNextPrintRoute = lazy(() => import('@/workshops-bc/InPersonNextPrintRoute'))
// Deck interno (/in-person-payments/pipeline) — 9 slides: TODA la demanda
// mundial del mundo físico separada en los dos negocios (POS Orchestration:
// BanCoppel, Conekta, Xcaret, Hertz, Quálitas, McDonald's, HRS · Tap to Pay:
// Banco Guayaquil, Conekta), $9.28M ARR con go-live por quarter, el riesgo de
// vender sin producto y las 5 necesidades para arrancar (Mastercard Cloud
// Commerce, owner, PCI MPoC, backlog, DevOps+SecOps). Sin slug: datos fijos
// en workshops-bc/lib/ipppipe-math.
const InPersonPipeRoute = lazy(() => import('@/workshops-bc/InPersonPipeRoute'))
const InPersonPipePrintRoute = lazy(() => import('@/workshops-bc/InPersonPipePrintRoute'))
// Pitch deck de la vertical (/in-person-payments/pitch) — 17 slides es/en con
// el orden narrativo del deck pre-seed de ElevenLabs: contexto → problema →
// solución → lo construido → quién lidera (Rasheed) → visión → TAM/SOM →
// BanCoppel → tracción ($9.28M) → segunda ola cross-sell → competencia (2×2,
// Aevi et al) → ventaja → inversión (certificaciones ≈$100K). Sin slug:
// datos fijos en workshops-bc/lib/ipppitch-math.
const InPersonPitchRoute = lazy(() => import('@/workshops-bc/InPersonPitchRoute'))
const InPersonPitchPrintRoute = lazy(() => import('@/workshops-bc/InPersonPitchPrintRoute'))
// Deck de PRODUCTO (/in-person-payments/products) — 11 slides EN: intro de
// Yuno como payments infrastructure (stats + pilares + logo wall del workshop
// deck), los dos productos de la vertical (POS Orchestration + Tap to Pay)
// con beneficios y cómo funcionan, one-infrastructure y cierre. Audiencia
// externa. Sin slug: el copy vive en workshops-bc/lib/ippprod-copy.
const InPersonProductsRoute = lazy(() => import('@/workshops-bc/InPersonProductsRoute'))
const InPersonProductsPrintRoute = lazy(() => import('@/workshops-bc/InPersonProductsPrintRoute'))

// Wallets BC deck (/wallet/:slug). Lazy-loaded — 15-slide business case for
// wallets whose unit of value is integration throughput (PSPs/quarter).
const WalletRoute = lazy(() => import('@/wallets-bc/WalletRoute'))
const WalletPrintRoute = lazy(() => import('@/wallets-bc/WalletPrintRoute'))

// Banks BC deck (/banks/:slug). Lazy-loaded — Banorte-design business case for
// banks/FIs: 6 levers over the same TPV, headline time-to-revenue (18mo→1mo).
const BanksRoute = lazy(() => import('@/banks-bc/BanksRoute'))
const BanksPrintRoute = lazy(() => import('@/banks-bc/BanksPrintRoute'))

// PSP BC deck (/psp/:slug). Lazy-loaded — build-vs-buy business case for PSPs /
// acquirers / gateways: whitelabel payment capability they'd otherwise build
// in-house (R&D avoided + time-to-market + performance levers).
const PspRoute = lazy(() => import('@/psp-bc/PspRoute'))
const PspPrintRoute = lazy(() => import('@/psp-bc/PspPrintRoute'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
})

// Auth guard for standalone routes rendered OUTSIDE MainLayout (which has its
// own redirect). Waits for the session to resolve so a refresh with a valid
// session doesn't bounce to /auth.
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }
  if (!user) {
    return <Navigate to="/auth" replace />
  }
  return <>{children}</>
}

// Router-level role guard for the Autom Cadence module. Defense-in-depth: the
// FeatureRoute above it already gates section_cadences, and AutomCadence itself
// keeps its own in-page outreach_admin guard — this adds a third layer so a
// non-admin never even mounts the page. Waits for the org context to finish
// loading (mirrors FeatureRoute) so permissions resolve before we redirect;
// otherwise the viewer defaults win on first render and bounce a real admin.
function RequireOutreachAdmin({ children }: { children: ReactNode }) {
  const { isLoading } = useOrg()
  const { outreach_admin } = usePermissions()
  if (isLoading) return null
  if (!outreach_admin) return <Navigate to="/" replace />
  return <>{children}</>
}

function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ModeProvider>
        <BrowserRouter>
          <AuthProvider>
            <OrgProvider>
            <CadenceProvider>
            <WorkflowProvider>
            <AccountMappingProvider>
            <CompanyResearchProvider>
            <BusinessCasesProvider>
            <AgentProvider>
            <AccountExecutiveProvider>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/org-select" element={<OrgSelect />} />
                <Route path="/invite/:token" element={<AcceptInvite />} />
                <Route path="/" element={<AppLauncher />} />
                <Route path="/mission-control" element={<MissionControl />} />
                {/* Agents — standalone app (not inside Chief Outreach) */}
                <Route path="/agents" element={<Agents />} />
                <Route path="/agents/:id" element={<AgentDetail />} />
                <Route path="/agents/workflows" element={<Workflows />} />
                <Route path="/agents/workflows/:id" element={<WorkflowBuilder />} />
                <Route path="/agents/workflows/:id/runs" element={<WorkflowRuns />} />

                {/* Chat with agents */}
                <Route path="/chat" element={<Chat />} />
                <Route path="/chat/:id" element={<Chat />} />
                {/* Presentaciones — standalone app (own dashboard for BCs, separate from Outreach) */}
                <Route path="/presentaciones" element={<RequireAuth><Presentaciones /></RequireAuth>} />

                {/* Tarjeta Presente — standalone planet: GBrain-style project brain
                    (cuentas, Gong, SF, decks, notas + preguntar con citas) */}
                <Route path="/tarjeta-presente" element={<RequireAuth><FeatureRoute flag="section_tarjeta_presente"><PageErrorBoundary><TarjetaPresente /></PageErrorBoundary></FeatureRoute></RequireAuth>} />

                {/* Stripe Sessions style decks — fully public, no auth/layout */}
                <Route path="/m/:slug" element={<Suspense fallback={null}><SSDeckRoute /></Suspense>} />
                <Route path="/m/:slug/pdf" element={<Suspense fallback={null}><SSDeckPrintRoute /></Suspense>} />

                {/* Workshop BC decks — fully public, no auth/layout */}
                <Route path="/workshop/:slug" element={<Suspense fallback={null}><WorkshopRoute /></Suspense>} />
                <Route path="/workshop/:slug/pdf" element={<Suspense fallback={null}><WorkshopPrintRoute /></Suspense>} />

                {/* Pricing-only mini-deck — public, no auth/layout */}
                <Route path="/pricing/:slug" element={<Suspense fallback={null}><PricingRoute /></Suspense>} />
                <Route path="/pricing/:slug/pdf" element={<Suspense fallback={null}><PricingPrintRoute /></Suspense>} />

                {/* Propuesta única (SaaS + Terminales POS + Payment Concierge) — public */}
                <Route path="/propuesta/:slug" element={<Suspense fallback={null}><ProposalRoute /></Suspense>} />
                <Route path="/propuesta/:slug/pdf" element={<Suspense fallback={null}><ProposalPrintRoute /></Suspense>} />

                {/* Propuesta simple (tx flat + SaaS + conciliación) — public */}
                <Route path="/precios/:slug" element={<Suspense fallback={null}><PreciosRoute /></Suspense>} />
                <Route path="/precios/:slug/pdf" element={<Suspense fallback={null}><PreciosPrintRoute /></Suspense>} />

                {/* Business case BanCoppel (MDR 1.70% → 1.50%) — public */}
                <Route path="/bc-bancoppel" element={<Suspense fallback={null}><BancoppelRoute /></Suspense>} />
                <Route path="/bc-bancoppel/pdf" element={<Suspense fallback={null}><BancoppelPrintRoute /></Suspense>} />

                {/* Propuesta comercial BanCoppel 2027 (pricing + mínimo + BC) — public */}
                <Route path="/propuesta-bancoppel" element={<Suspense fallback={null}><PropuestaBancoppelRoute /></Suspense>} />
                <Route path="/propuesta-bancoppel/pdf" element={<Suspense fallback={null}><PropuestaBancoppelPrintRoute /></Suspense>} />

                {/* Propuesta comercial Grupo Quálitas (3 slides) — public */}
                <Route path="/qualitas" element={<Suspense fallback={null}><QualitasRoute /></Suspense>} />
                <Route path="/qualitas/pdf" element={<Suspense fallback={null}><QualitasPrintRoute /></Suspense>} />

                {/* Deck interno C-Level — vertical In-Person Payments (10 slides, es + en) */}
                <Route path="/in-person-payments" element={<Suspense fallback={null}><InPersonRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/pdf" element={<Suspense fallback={null}><InPersonPrintRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/en" element={<Suspense fallback={null}><InPersonRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/en/pdf" element={<Suspense fallback={null}><InPersonPrintRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/next-steps" element={<Suspense fallback={null}><InPersonNextRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/next-steps/pdf" element={<Suspense fallback={null}><InPersonNextPrintRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/next-steps/en" element={<Suspense fallback={null}><InPersonNextRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/next-steps/en/pdf" element={<Suspense fallback={null}><InPersonNextPrintRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/products" element={<Suspense fallback={null}><InPersonProductsRoute /></Suspense>} />
                <Route path="/in-person-payments/products/pdf" element={<Suspense fallback={null}><InPersonProductsPrintRoute /></Suspense>} />
                <Route path="/in-person-payments/pipeline" element={<Suspense fallback={null}><InPersonPipeRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/pipeline/pdf" element={<Suspense fallback={null}><InPersonPipePrintRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/pipeline/en" element={<Suspense fallback={null}><InPersonPipeRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/pipeline/en/pdf" element={<Suspense fallback={null}><InPersonPipePrintRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/pitch" element={<Suspense fallback={null}><InPersonPitchRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/pitch/pdf" element={<Suspense fallback={null}><InPersonPitchPrintRoute lang="es" /></Suspense>} />
                <Route path="/in-person-payments/pitch/en" element={<Suspense fallback={null}><InPersonPitchRoute lang="en" /></Suspense>} />
                <Route path="/in-person-payments/pitch/en/pdf" element={<Suspense fallback={null}><InPersonPitchPrintRoute lang="en" /></Suspense>} />

                {/* Wallets BC decks — fully public, no auth/layout */}
                <Route path="/wallet/:slug" element={<Suspense fallback={null}><WalletRoute /></Suspense>} />
                <Route path="/wallet/:slug/pdf" element={<Suspense fallback={null}><WalletPrintRoute /></Suspense>} />

                {/* Banks BC decks — fully public, no auth/layout */}
                <Route path="/banks/:slug" element={<Suspense fallback={null}><BanksRoute /></Suspense>} />
                <Route path="/banks/:slug/pdf" element={<Suspense fallback={null}><BanksPrintRoute /></Suspense>} />

                {/* PSP BC decks — fully public, no auth/layout */}
                <Route path="/psp/:slug" element={<Suspense fallback={null}><PspRoute /></Suspense>} />
                <Route path="/psp/:slug/pdf" element={<Suspense fallback={null}><PspPrintRoute /></Suspense>} />
                <Route element={<MainLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/cadences" element={<FeatureRoute flag="section_cadences"><Cadences /></FeatureRoute>} />
                  <Route path="/cadences/:id" element={<FeatureRoute flag="section_cadences"><CadenceBuilder /></FeatureRoute>} />
                  <Route path="/cadences/:cadenceId/step/:stepId" element={<FeatureRoute flag="section_cadences"><LeadStepExecution /></FeatureRoute>} />
                  <Route path="/cadence-flow" element={<FeatureRoute flag="section_cadences"><CadenceFlow /></FeatureRoute>} />
                  <Route path="/cadence-flow/:id" element={<FeatureRoute flag="section_cadences"><CadenceFlow /></FeatureRoute>} />
                  <Route path="/autom-cadence" element={<FeatureRoute flag="section_cadences"><RequireOutreachAdmin><AutomCadence /></RequireOutreachAdmin></FeatureRoute>} />
                  <Route path="/autom-cadence/new" element={<FeatureRoute flag="section_cadences"><RequireOutreachAdmin><NewAutomCadence /></RequireOutreachAdmin></FeatureRoute>} />
                  <Route path="/autom-cadence/:id" element={<FeatureRoute flag="section_cadences"><RequireOutreachAdmin><AutomCadence /></RequireOutreachAdmin></FeatureRoute>} />
                  <Route path="/workflows" element={<FeatureRoute flag="section_workflows"><Workflows /></FeatureRoute>} />
                  <Route path="/workflows/:id" element={<FeatureRoute flag="section_workflows"><WorkflowBuilder /></FeatureRoute>} />
                  <Route path="/workflows/:id/runs" element={<FeatureRoute flag="section_workflows"><WorkflowRuns /></FeatureRoute>} />
                  <Route path="/account-mapping" element={<FeatureRoute flag="section_account_mapping"><AccountMapping /></FeatureRoute>} />
                  <Route path="/account-mapping/icp-profiles/:id" element={<FeatureRoute flag="section_account_mapping"><PageErrorBoundary><ICPProfileDetail /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/account-mapping/:id" element={<FeatureRoute flag="section_account_mapping"><AccountMapDetail /></FeatureRoute>} />
                  <Route path="/buyer-personas" element={<FeatureRoute flag="section_account_mapping"><BuyerPersonas /></FeatureRoute>} />
                  <Route path="/company-registry" element={<FeatureRoute flag="section_company_registry"><CompanyRegistry /></FeatureRoute>} />
                  <Route path="/company-research" element={<FeatureRoute flag="section_company_research"><PageErrorBoundary><CompanyResearch /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/company-research/:id" element={<FeatureRoute flag="section_company_research"><PageErrorBoundary><ResearchProjectDetail /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/business-cases" element={<FeatureRoute flag="section_business_cases"><BusinessCases /></FeatureRoute>} />
                  <Route path="/business-cases/new" element={<FeatureRoute flag="section_business_cases"><BusinessCaseNew /></FeatureRoute>} />
                  <Route path="/business-cases/templates/:id" element={<FeatureRoute flag="section_business_cases"><BusinessCaseTemplateEditor /></FeatureRoute>} />
                  <Route path="/business-cases/generate" element={<FeatureRoute flag="section_business_cases"><BusinessCaseGenerate /></FeatureRoute>} />
                  <Route path="/account-executive" element={<FeatureRoute flag="section_account_executive"><PageErrorBoundary><AccountExecutive /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/account-executive/crm" element={<FeatureRoute flag="section_account_executive"><PageErrorBoundary><CRMPipeline /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/account-executive/:id" element={<FeatureRoute flag="section_account_executive"><PageErrorBoundary><AccountExecutiveDetail /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/account-executive/calendar" element={<FeatureRoute flag="section_account_executive"><PageErrorBoundary><AccountExecutiveCalendar /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/account-executive/presentations" element={<FeatureRoute flag="section_account_executive"><PageErrorBoundary><Presentaciones embedded /></PageErrorBoundary></FeatureRoute>} />
                  <Route path="/lead-search" element={<FeatureRoute flag="section_lead_search"><LeadSearch /></FeatureRoute>} />
                  <Route path="/leads" element={<FeatureRoute flag="section_leads"><Leads /></FeatureRoute>} />
                  <Route path="/inbox" element={<FeatureRoute flag="section_linkedin_inbox"><LinkedInInbox /></FeatureRoute>} />
                  <Route path="/templates" element={<FeatureRoute flag="section_templates"><Templates /></FeatureRoute>} />
                  <Route path="/ai-prompts" element={<FeatureRoute flag="section_ai_prompts"><AIPrompts /></FeatureRoute>} />
                  <Route path="/notifications" element={<FeatureRoute flag="section_notifications"><Notifications /></FeatureRoute>} />
                  <Route path="/outreach" element={<OutreachActivity />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/outreach" element={<OutreachControl />} />
                  <Route path="/settings/qa-policy" element={<Navigate to="/autom-cadence?tab=policy" replace />} />
                  <Route path="/settings/touch-angles" element={<Navigate to="/autom-cadence?tab=angles" replace />} />
                  <Route path="/settings/operational-limits" element={<Navigate to="/autom-cadence?tab=limits" replace />} />
                  <Route path="/settings/organization" element={<OrgSettings />} />
                  <Route path="/settings/members" element={<OrgMembers />} />
                  <Route path="/settings/salesforce/callback" element={<SalesforceCallback />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/logs" element={<AdminLogs />} />
                  <Route path="/admin/metrics" element={<AdminMetrics />} />
                  <Route path="/super-admin/organizations" element={<SuperAdminOrgs />} />
                </Route>
              </Routes>
              <Toaster position="bottom-right" />
            </AccountExecutiveProvider>
            </AgentProvider>
            </BusinessCasesProvider>
            </CompanyResearchProvider>
            </AccountMappingProvider>
            </WorkflowProvider>
            </CadenceProvider>
            </OrgProvider>
          </AuthProvider>
        </BrowserRouter>
        </ModeProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
